import * as React from 'react';

import Link from 'next/link';

import { User } from '@supabase/supabase-js';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { siteConfig } from './marketing-config';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from './ui/navigation-menu';

export default function NavigationMenuDemo({ user }: { user: User | null }) {
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      html body[data-scroll-locked],
      body[data-scroll-locked]:not(#___never_match__) {
        padding-right: 0px !important;
        margin-right: 0px !important;
        overflow: auto !important;
        --removed-body-scroll-bar-size: 0px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <NavigationMenu>
      <NavigationMenuList>
        {siteConfig.header.map((item, index) => (
          <NavigationMenuItem key={index}>
            {'trigger' in item ? (
              <>
                <NavigationMenuTrigger className="text-white">
                  {item.trigger}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul
                    className={`grid gap-3 p-6 ${
                      item.content.main
                        ? 'md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]'
                        : 'w-[400px] md:w-[500px] md:grid-cols-2 lg:w-[600px]'
                    }`}
                  >
                    {item.content.main && (
                      <li className="row-span-3">
                        <NavigationMenuLink asChild>
                          <Link
                            className="flex h-full w-full select-none flex-col justify-end rounded-md bg-primary/10 from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                            href={item.content.main.href}
                          >
                            {item.content.main.icon}
                            <div className="mb-2 mt-4 text-lg font-medium">
                              {item.content.main.title}
                            </div>
                            <p className="text-sm leading-tight text-muted-foreground">
                              {item.content.main.description}
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    )}
                    {item.content.items.map((subItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        href={subItem.href}
                        title={subItem.title}
                        className="hover:bg-primary/10"
                      >
                        {subItem.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink
                asChild
                className={navigationMenuTriggerStyle()}
              >
                <Link href={item.href ?? ''} target="_arya">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
        {!user && (
          <NavigationMenuItem>
            <Link
              href="/auth/sign-in"
              className="ml-16 h-10 px-4 py-2 text-sm font-medium text-white"
            >
              Login
            </Link>
          </NavigationMenuItem>
        )}
        <NavigationMenuItem>
          <Button
            asChild
            className={cn(
              'hover:bg-[hsl(var(--action-white-hover))]',
              `${user ? 'ml-10' : 'ml-4'} flex h-11 w-full rounded-3xl bg-white text-foreground dark:text-background sm:w-auto sm:text-sm sm:font-semibold`,
            )}
          >
            <Link href="/contact">Contact Us</Link>
          </Button>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className,
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});

ListItem.displayName = 'ListItem';
